import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import axiosInstance from "@/api/axiosInstance";
import { type AdminCategory } from "./useAdmin";

// Categories CRUD hooks
export const useCategories = (enabled: boolean = true) => {
    return useQuery<AdminCategory[]>({
        queryKey: ['admin', 'categories'],
        queryFn: async () => {
            const { data } = await axiosInstance.get('/products/categories/');
            return data.results || data;
        },
        enabled,
    });
};

export const useCategory = (id: number, enabled: boolean = true) => {
    return useQuery<AdminCategory>({
        queryKey: ['admin', 'categories', id],
        queryFn: async () => {
            const { data } = await axiosInstance.get(`/products/categories/${id}/`);
            return data;
        },
        enabled: enabled && !!id,
    });
};

export const useCreateCategory = () => {
    const queryClient = useQueryClient();

    return useMutation<AdminCategory, Error, Omit<AdminCategory, 'id' | 'created_at' | 'updated_at'>>({
        mutationFn: async (categoryData) => {
            const { data } = await axiosInstance.post('/products/categories/', categoryData);
            return data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['admin', 'categories'] });
        },
    });
};

export const useUpdateCategory = () => {
    const queryClient = useQueryClient();

    return useMutation<AdminCategory, Error, { id: number; data: Partial<Omit<AdminCategory, 'id' | 'created_at' | 'updated_at'>> }>({
        mutationFn: async ({ id, data }) => {
            const response = await axiosInstance.patch(`/products/categories/${id}/`, data);
            return response.data;
        },
        onSuccess: (_, { id }) => {
            queryClient.invalidateQueries({ queryKey: ['admin', 'categories'] });
            queryClient.invalidateQueries({ queryKey: ['admin', 'categories', id] });
        },
    });
};

export const useDeleteCategory = () => {
    const queryClient = useQueryClient();

    return useMutation<void, Error, number>({
        mutationFn: async (id: number) => {
            await axiosInstance.delete(`/products/categories/${id}/`);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['admin', 'categories'] });
        },
    });
};
